import { Button } from '@heidi/design-system/button';
import { TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ListTodo } from 'lucide-react';
import { useIntl } from 'react-intl';

import { agentTodoMessages } from '../../../../data/projects/timeline/agent-todo/agentTodo.messages';

import type { JSX } from 'react';

const PLAN_PROGRESS_SEPARATOR = '·';

export interface PlanProgressBarProps {
  /** Plan title, or the chat title when the plan has none. */
  title: string;
  completed: number;
  total: number;
  currentStepTitle?: string | null;
  showStepTitle?: boolean;
  /** Open state of the plan this bar toggles, when it toggles one. */
  expanded?: boolean;
  onSelect: () => void;
  className?: string;
}

/**
 * Slim summary of the plan's progress. Presentational — {@link PlanProgressDock}
 * owns when it appears and what clicking it does.
 */
export const PlanProgressBar = ({
  title,
  completed,
  total,
  currentStepTitle,
  showStepTitle = true,
  expanded,
  onSelect,
  className,
}: PlanProgressBarProps): JSX.Element => {
  const intl = useIntl();
  const step = showStepTitle ? currentStepTitle : null;

  return (
    <Button
      variant="surface"
      size="sm"
      onClick={onSelect}
      aria-expanded={expanded}
      data-testid="plan-progress-bar"
      className={cn(
        'w-full justify-start overflow-hidden border border-border shadow-sm motion-safe:duration-200 motion-safe:ease-out-quart motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-2',
        // Overrides the recipe's `transition-all`: the corner must square off
        // in the same frame the plan mounts, not 200ms later.
        'transition-colors',
        // The `surface` variant fades to 50% on hover, press and open, which
        // lets the transcript bleed through this overlay. Both themes are named
        // because the variant's `newBrandDark:` classes outrank a bare `hover:`.
        'hover:bg-surface-hover active:bg-surface-pressed aria-expanded:bg-surface-hover newBrandDark:hover:bg-surface-hover newBrandDark:active:bg-surface-pressed newBrandDark:aria-expanded:bg-surface-hover',
        className
      )}
    >
      {/* Prefix rather than an aria-label, which would replace the accessible
          name and hide the progress. */}
      <span className="sr-only">
        {intl.formatMessage(agentTodoMessages.showPlanSteps)}
      </span>
      <ListTodo className="size-3.5 shrink-0 text-text-secondary" aria-hidden />
      {/* Narrow widths clip from the right: the step title goes first (the only
          `flex-1`), then the plan title; the count never shrinks. */}
      <TypographyP2 className="min-w-0 truncate font-medium text-text-primary">
        {title}
      </TypographyP2>
      <TypographyP2 className="shrink-0 text-text-secondary">
        {intl.formatMessage(agentTodoMessages.planProgressSteps, { completed, total })}
      </TypographyP2>
      {step ? (
        <span className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
          <span className="shrink-0 text-text-tertiary" aria-hidden>
            {PLAN_PROGRESS_SEPARATOR}
          </span>
          <TypographyP2 className="min-w-0 truncate text-start text-text-secondary">
            {step}
          </TypographyP2>
        </span>
      ) : null}
    </Button>
  );
};
